import { useEffect, useRef, useState } from 'react'
import { Layers, Loader2, Map as MapIcon } from 'lucide-react'
import type { Map as MapboxMap, Marker as MapboxMarker } from 'mapbox-gl'
import type { GeoJsonObject } from '@/shared/api/types'
import { cn } from '@/lib/utils'
import { hasMapbox, loadMapbox, styleUrl, KENYA_BOUNDS, type MapStyleChoice } from './mapbox'
import { extractRings, ringBounds } from './geo'
import { VectorPreview } from './VectorPreview'

/**
 * The one map component.
 *
 * Two modes, because the domain has exactly two geometries:
 *
 * - `boundary` — counties, constituencies and wards are **areas**. Draws the
 *   polygon and fits to it.
 * - `point` — a polling station is a **place**. Draws one marker; when `editable`
 *   it can be dragged or placed by clicking, which is the only sane way to enter
 *   coordinates for "the primary school past the market".
 *
 * Which mode a unit type takes is not decided here — it comes from
 * `geometryKindOf()` in `shared/api/types`, so there is one rule, and the backend
 * will eventually serve it (`geometryKind` on `UnitResponse`, prompt 9).
 *
 * **No token is a supported state.** Everything degrades to `VectorPreview` for a
 * boundary and to a plain notice for a point; the numeric latitude and longitude
 * inputs beside the map keep working either way, so nobody is ever blocked on a
 * third party being reachable.
 */
const STYLE_CHOICES: { value: MapStyleChoice; label: string; title: string }[] = [
  { value: 'theme', label: 'Map', title: "The app's basemap — quiet, follows the theme" },
  { value: 'streets', label: 'Streets', title: 'Standard street map, with road names' },
  { value: 'satellite', label: 'Satellite', title: 'Aerial imagery with street labels' },
]

export function UnitMap({
  mode,
  boundary,
  lat,
  lon,
  editable = false,
  onPick,
  markers,
  onMarkerClick,
  label,
  className,
  allowSatellite = true,
  cooperativeGestures = false,
}: {
  mode: 'boundary' | 'point'
  boundary?: GeoJsonObject | null
  lat?: number | null
  lon?: number | null
  editable?: boolean
  onPick?: (lat: number, lon: number) => void
  /**
   * MANY places at once — an incident map, not the single `lat`/`lon` pin.
   *
   * Separate from the pin on purpose: the pin is an INPUT (draggable, click-to-place) and
   * these are OUTPUT (read-only, clickable to open the thing they stand for). Overloading
   * one prop to mean both would make "click the map" ambiguous.
   */
  markers?: { id: string; lat: number; lon: number; color?: string; size?: number; title?: string }[]
  onMarkerClick?: (id: string) => void
  label: string
  /** Height utility — the map fills its container, so give it one. */
  className?: string
  allowSatellite?: boolean
  /** Require ctrl/⌘ + wheel to zoom. Off — see the note where the map is constructed. */
  cooperativeGestures?: boolean
}) {
  const host = useRef<HTMLDivElement>(null)
  const map = useRef<MapboxMap | null>(null)
  const marker = useRef<MapboxMarker | null>(null)
  /** Latest `onPick` without re-registering the map's click handler every render. */
  const pick = useRef(onPick)
  pick.current = onPick

  const [style, setStyle] = useState<MapStyleChoice>('theme')
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'failed'>(
    hasMapbox() ? 'loading' : 'idle',
  )
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'))

  // The theme is set once at boot by the inline script in index.html, but a
  // MutationObserver costs nothing and means a future theme switcher does not
  // leave a white map inside a dark shell.
  useEffect(() => {
    const el = document.documentElement
    const obs = new MutationObserver(() => setDark(el.classList.contains('dark')))
    obs.observe(el, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])

  // ── create the map once ───────────────────────────────────────────────────
  useEffect(() => {
    if (!hasMapbox() || !host.current) return
    let cancelled = false
    setStatus('loading')

    loadMapbox().then((mapboxgl) => {
      if (cancelled || !host.current) return
      const m = new mapboxgl.Map({
        container: host.current,
        style: styleUrl(style, dark),
        bounds: KENYA_BOUNDS,
        fitBoundsOptions: { padding: 16 },
        // Attribution is a Mapbox terms-of-service requirement, not a decoration.
        // It is on by default; do not pass `attributionControl: false`.
        /*
           Plain wheel-zoom, not ctrl+wheel.

           `cooperativeGestures` exists to stop a map swallowing page scroll on a long
           marketing page. Every map in this app sits in a fixed-height panel inside a
           shell that scrolls its own container, so the trap it guards against does not
           arise — and the guard's cost is that zooming appears not to work at all: the
           user scrolls, the map ignores it, and nothing says why. Callers who genuinely
           embed a map in a long scrolling column can set it back.
        */
        cooperativeGestures,
      })
      m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')
      m.addControl(new mapboxgl.ScaleControl({ unit: 'metric' }), 'bottom-left')
      m.on('load', () => { if (!cancelled) setStatus('ready') })
      m.on('error', () => { if (!cancelled) setStatus('failed') })
      map.current = m
    }).catch(() => { if (!cancelled) setStatus('failed') })

    return () => {
      cancelled = true
      marker.current?.remove()
      marker.current = null
      map.current?.remove()
      map.current = null
    }
    // Intentionally once: style and theme are applied by the effect below, and
    // recreating the map on every prop change would flash and lose the viewport.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
     Re-measure whenever the container changes size.

     `mapboxgl.Map` sizes its canvas once, at construction, from whatever the container
     measured then. This component mounts inside things that are not their final size at
     that moment — a wizard step that has just been switched to, a drawer sliding in, a
     panel whose stylesheet has not landed yet — and mapbox does not watch for it. Without
     this the canvas keeps its first measurement forever.
  */
  useEffect(() => {
    const el = host.current
    if (!el || status !== 'ready') return
    const obs = new ResizeObserver(() => map.current?.resize())
    obs.observe(el)
    return () => obs.disconnect()
  }, [status])

  // ── style / theme changes ─────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || status === 'loading') return
    m.setStyle(styleUrl(style, dark))
    // setStyle drops every source and layer; `style.load` is where they go back.
  }, [style, dark, status])

  // ── boundary layer ────────────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || mode !== 'boundary') return

    const draw = () => {
      if (!boundary) return
      const existing = m.getSource('unit-boundary')
      if (existing) {
        ;(existing as { setData: (d: unknown) => void }).setData(boundary)
      } else {
        m.addSource('unit-boundary', { type: 'geojson', data: boundary as never })
        m.addLayer({
          id: 'unit-boundary-fill',
          type: 'fill',
          source: 'unit-boundary',
          paint: { 'fill-color': '#7B3F00', 'fill-opacity': 0.18 },
        })
        m.addLayer({
          id: 'unit-boundary-line',
          type: 'line',
          source: 'unit-boundary',
          paint: { 'line-color': '#7B3F00', 'line-width': 2 },
        })
      }
      const b = ringBounds(extractRings(boundary))
      if (b) {
        m.fitBounds([[b.minLon, b.minLat], [b.maxLon, b.maxLat]], {
          padding: 24, duration: 0, maxZoom: 12,
        })
      }
    }

    if (m.isStyleLoaded()) draw()
    m.on('style.load', draw)
    return () => { m.off('style.load', draw) }
  }, [boundary, mode, status])

  // ── point marker ──────────────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || mode !== 'point' || status === 'loading') return
    let cancelled = false

    void loadMapbox().then((mapboxgl) => {
      if (cancelled || !map.current) return
      const has = typeof lat === 'number' && typeof lon === 'number'
      if (!has) {
        marker.current?.remove()
        marker.current = null
        return
      }
      if (!marker.current) {
        marker.current = new mapboxgl.Marker({ color: '#7B3F00', draggable: editable })
          .setLngLat([lon as number, lat as number])
          .addTo(map.current)
        if (editable) {
          marker.current.on('dragend', () => {
            const p = marker.current?.getLngLat()
            if (p) pick.current?.(Number(p.lat.toFixed(6)), Number(p.lng.toFixed(6)))
          })
        }
      } else {
        marker.current.setLngLat([lon as number, lat as number])
      }
      map.current.easeTo({ center: [lon as number, lat as number], zoom: 14, duration: 0 })
    })

    return () => { cancelled = true }
  }, [lat, lon, mode, editable, status])

  // ── click-to-place ────────────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || !editable || mode !== 'point') return
    const onClick = (e: { lngLat: { lat: number; lng: number } }) => {
      pick.current?.(Number(e.lngLat.lat.toFixed(6)), Number(e.lngLat.lng.toFixed(6)))
    }
    m.on('click', onClick)
    m.getCanvas().style.cursor = 'crosshair'
    return () => { m.off('click', onClick) }
  }, [editable, mode, status])

  /*
     Many markers.

     Rebuilt wholesale whenever the set changes rather than diffed: an incident map redraws
     when a filter changes, the counts are in the dozens, and a diff would be more code
     defending a saving nobody can perceive. The fit is applied ONCE per distinct set so
     panning is not yanked back on every unrelated re-render.
  */
  const manyMarkers = useRef<MapboxMarker[]>([])
  const markerKey = (markers ?? []).map((m) => `${m.id}:${m.lat}:${m.lon}:${m.color ?? ''}`).join('|')
  const clickMarker = useRef(onMarkerClick)
  clickMarker.current = onMarkerClick

  useEffect(() => {
    const m = map.current
    if (!m || status !== 'ready') return
    let cancelled = false

    void loadMapbox().then((mapboxgl) => {
      if (cancelled || !map.current) return
      manyMarkers.current.forEach((mk) => mk.remove())
      manyMarkers.current = []
      // Draw the BIGGEST last so it sits on top: a CRITICAL marker hidden behind three
      // LOW ones is the one incident that must not be hidden.
      const rows = [...(markers ?? [])].sort((a, b) => (a.size ?? 14) - (b.size ?? 14))
      if (rows.length === 0) return

      for (const row of rows) {
        const el = document.createElement('button')
        el.type = 'button'
        el.title = row.title ?? ''
        el.setAttribute('aria-label', row.title ?? 'Incident')
        // Size is a SECOND channel carrying the same ordered value as the colour, so the
        // order survives colour-blindness, greyscale and a busy basemap.
        const size = row.size ?? 14
        el.style.cssText = `width:${size}px;height:${size}px;border-radius:9999px;cursor:pointer;`
          + `background:${row.color ?? 'var(--ordinal-4)'};`
          // A 2px surface ring, so overlapping markers stay countable.
          + 'border:2px solid var(--card);box-shadow:0 0 0 1px rgba(0,0,0,.35);padding:0'
        el.addEventListener('click', (e) => {
          e.stopPropagation()
          clickMarker.current?.(row.id)
        })
        manyMarkers.current.push(
          new mapboxgl.Marker({ element: el }).setLngLat([row.lon, row.lat]).addTo(map.current))
      }

      const lons = rows.map((r) => r.lon)
      const lats = rows.map((r) => r.lat)
      map.current.fitBounds(
        [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
        { padding: 48, duration: 0, maxZoom: 11 })
    })

    return () => {
      cancelled = true
      manyMarkers.current.forEach((mk) => mk.remove())
      manyMarkers.current = []
    }
    // `markerKey` stands in for `markers`: the array identity changes every render, and
    // depending on it would rebuild every marker forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerKey, status])

  // ── fallbacks ─────────────────────────────────────────────────────────────
  if (!hasMapbox() || status === 'failed') {
    return (
      <Fallback
        mode={mode}
        boundary={boundary}
        lat={lat}
        lon={lon}
        label={label}
        className={className}
        reason={status === 'failed'
          ? 'The map could not load. The coordinates below are unaffected.'
          : 'No map key is configured — set AXIS_MAPBOX_TOKEN for a basemap.'}
      />
    )
  }

  return (
    <div className={cn('relative border border-border rounded-md overflow-hidden', className)}>
      {/*
         `h-full w-full` as well as `inset-0`, and that is not belt-and-braces.

         `mapbox-gl.css` is imported by `mapbox-impl.ts`, which is loaded LAZILY — so it
         arrives after the app's stylesheet and wins the cascade. Its
         `.mapboxgl-map { position: relative }` lands on this very element the moment the
         map is constructed, which cancels the `absolute` that `inset-0` depends on for its
         height. Every child mapbox inserts is absolutely positioned, so the host collapses
         to zero, the parent's `overflow-hidden` clips the canvas away, and the result is a
         blank panel with a fully loaded map behind it and NOTHING in the console: tiles
         200, `load` fires, `status` reaches `ready`.

         An explicit height does not care what `position` ends up being.
      */}
      <div ref={host} className="absolute inset-0 h-full w-full"
        aria-label={`Map of ${label}`} role="img" />

      {status === 'loading' && (
        <div className="absolute inset-0 grid place-items-center bg-muted/40 text-erp-xs text-muted-foreground">
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading map…
          </span>
        </div>
      )}

      {/*
         Three basemaps, because they answer three questions. The themed one is quiet
         enough to sit inside the app's chrome but drops most street names; `streets` has
         the names, which is what somebody placing "the roadblock on Ngong Road" needs;
         satellite has the ground and no labels. A two-way toggle made the middle one
         unreachable.
      */}
      {allowSatellite && (
        <div className="absolute top-2 left-2 z-10 inline-flex rounded-md border border-border
                        bg-background/90 backdrop-blur overflow-hidden">
          <Layers className="h-3 w-3 self-center ml-2 mr-1 opacity-60" aria-hidden />
          {STYLE_CHOICES.map((choice) => (
            <button key={choice.value} type="button"
              onClick={() => setStyle(choice.value)}
              aria-pressed={style === choice.value}
              title={choice.title}
              className={cn(
                'h-7 px-2 text-erp-xs',
                style === choice.value
                  ? 'bg-chocolate-soft/60 text-chocolate font-medium'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}>
              {choice.label}
            </button>
          ))}
        </div>
      )}

      {editable && mode === 'point' && status === 'ready' && (
        <p className="absolute bottom-2 right-2 z-10 px-2 py-1 rounded-md
                      bg-background/90 backdrop-blur border border-border
                      text-erp-xs text-muted-foreground pointer-events-none">
          Click the map to place the station, or drag the pin.
        </p>
      )}
    </div>
  )
}

function Fallback({
  mode, boundary, lat, lon, label, className, reason,
}: {
  mode: 'boundary' | 'point'
  boundary?: GeoJsonObject | null
  lat?: number | null
  lon?: number | null
  label: string
  className?: string
  reason: string
}) {
  const rings = mode === 'boundary' ? extractRings(boundary) : []

  if (rings.length > 0) {
    return (
      <div className="space-y-1.5">
        <VectorPreview rings={rings} label={label} className={className ?? 'h-48'} />
        <p className="text-erp-xs text-muted-foreground">{reason} Showing the outline only.</p>
      </div>
    )
  }

  return (
    <div className={cn(
      'border border-dashed border-border rounded-md grid place-items-center px-4 text-center',
      className ?? 'h-48',
    )}>
      <div>
        <MapIcon className="h-4 w-4 mx-auto text-muted-foreground" />
        <p className="text-erp-sm text-muted-foreground mt-1.5">{reason}</p>
        {mode === 'point' && typeof lat === 'number' && typeof lon === 'number' && (
          <p className="text-erp-xs font-mono text-muted-foreground mt-1">
            {lat.toFixed(6)}, {lon.toFixed(6)}
          </p>
        )}
      </div>
    </div>
  )
}
